import {Box, Button, Tooltip, Typography} from '@mui/material';
import {FC, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {linkButtonSx} from './EmployeeCell.sx';
import {EmployeeCellType} from './EmployeeCell.types';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {UserTypeEnum} from 'src/enums';
import {useMissingBexio} from 'src/hooks/useMissingBexio';
import {blankUserPhoto} from 'src/utils/consts';
import {imageWrapperSx} from '../UserCell/UserCell.sx';

export const EmployeeCell: FC<EmployeeCellType> = ({data, isEmployeeUser}) => {
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const intl = useIntl();

    const navigate = useNavigate();

    const employeeNameLabel = useMemo<string>(() => {
        if (!data) {
            return '';
        }

        const firstName = data?.firstName ?? data.employee?.firstName ?? '';
        const lastName = data?.lastName ?? data.employee?.lastName ?? '';

        return `${firstName} ${lastName}`.trim();
    }, [data]);

    // This cell always represents an employee, so the red "no Bexio" flag applies
    // only when the employee pays for their own consumption (EMPLOYEE mode).
    // Company-paid employees stay the normal (blue) colour.
    // consumptionPaymentMode may sit at the top level (EmployeesTable rows) or on
    // the nested employee (user rows).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const paymentMode = (data as any)?.consumptionPaymentMode ?? data?.employee?.consumptionPaymentMode;
    const showMissingBexio = useMissingBexio({
        hasBexio: !!data?.bexioDataList?.length,
        userType: UserTypeEnum.EMPLOYEE,
        paymentMode,
    });

    const handleLocate = () => {
        const employeeId = data?.employee?.id ?? data?.id ?? 0;
        const businessIdParam = data?.business?.id ?? businessId ?? 0;
        const companyIdParam = data.employee?.company?.id ?? authInfo?.company?.id ?? 0;


        navigate(
            `/app/users/employee/${employeeId}?businessId=${businessIdParam}&companyId=${companyIdParam}`
        );
    };

    if (isEmployeeUser) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                <Box sx={imageWrapperSx}>
                    <img
                        src={data.avatar?.url || blankUserPhoto}
                        alt={`${data?.firstName || ''} ${data?.lastName || ''}`}
                    />
                </Box>
                <Typography
                    variant="subtitle2"
                    color={showMissingBexio ? 'error.main' : 'primary.dark'}
                >
                    {employeeNameLabel}
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Tooltip
                title={showMissingBexio ? intl.formatMessage({id: 'noBexioIntegration'}) : ''}
                arrow
            >
                <Button
                    onClick={handleLocate}
                    sx={linkButtonSx}
                >
                    <Box sx={imageWrapperSx}>
                        <img
                            src={data.avatar?.url || blankUserPhoto}
                            alt={`${data?.firstName || ''} ${data?.lastName || ''}`}
                        />
                    </Box>
                    <Typography
                        variant="subtitle2"
                        color={showMissingBexio ? 'error.main' : 'primary.main'}
                    >
                        {employeeNameLabel}
                    </Typography>
                </Button>
            </Tooltip>
        </Box>
    );
};
